import React, { FC, useCallback } from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "react-error-boundary";

import { appSettings } from "../../config/app";
import useIsSmallScreen from "@/hooks/useIsSmallScreen";
import ProductDetailImage from "./productDetailImage";
import SyteSimilarItems from "../../components/syte";
import Page from "../../components/page";
import DeliveryAndReturnMobile from "../../components/delivery/deliveryAndReturnInfoMobile";
import View from "../../components/common/view";

import ProductMobileSizes from "./mobileSizes";
import BrandDetails from "./brandDetails";
import PDPHelmet from "./helmet";
import ProductPriceSection from "./priceSection";
import TopMobileContent from "./topMobileContent";
import PageSections from "./pageSections";
import PdpSyteDiscoveryBanner from "./syteDiscoveryBanner";
import RecentlyViewedProducts from "./recentlyViewedProducts";
import LoyaltyPointsInfo from "./loyaltyPoints";
import ProductSizeChart from "./sizeChart/sizeChartContent/productSizeChart";
import ProductWebSizes from "@/components/product/webSizes";
import { ProductHighlights, MobileHighlightsWrapper } from "./highlights";
import {
  ProductQuantity,
  ProductQuantityMob
} from "@/components/product/quantity";
import {
  AddToCart,
  MobileAddToCart
} from "@/components/product/addToCartButton";
import ProductShippingOptions from "@/components/product/webSizes/productShippingOptions";
import SuccessAddedToCartMessage from "@/components/product/addToCartButton/successMessage";
import IsNotPriceAvailableMessage from "@/components/product/isNotPriceAvailableMessage";
import ModelDetails from "@/components/product/modelDetails";
import PayLaterOptions from "./payLaterOptions";
import SizeChart from "./sizeChart";
import ScrollToTop from "./scrollToTop";
import ProductDetailsBreadcrumbs from "./breadcrumbs";
import AddToWishlistButton from "@/components/product/addToWishlistButton";
import DialogModal from "../../components/dialogModal";
import ProductErrorInfo from "./productErrorInfo";
import RelatedProducts from "./relatedProducts";
import GlobalShipping from "./globalShipping";

import { ProductDetailsLayoutProps } from "./types";

import "./style.scss";

const ProductDetailsLayout: FC<ProductDetailsLayoutProps> = ({
  topSections,
  firstSections,
  secondSections,
  language,
  productId,
  itemDetailWrapperRef,
  activeSizeType,
  setActiveSizeType,
  selectedSizeFullData,
  setSelectedSizeFullData,
  setShowProductSizeChart,
  setShowShippingOptions,
  setSelectedQty,
  selectedQty,
  onAddToCartClickHandler,
  addToCart,
  getCartData,
  item,
  isSuccessBlockOpen,
  closeSuccessAddedToCartBlock,
  sameSizeInCartCount,
  payLaterOptions,
  onSizeSelectionHandler,
  openMultipleSizes,
  setOpenMultipleSizes,
  countryId,
  subcategory,
  captionImageURL,
  configSyteSettings,
  productSizeChart,
  showProductSizeChart,
  showShippingOptions,
  goToCart,
  alertMessage,
  setAlertMessage,
  isRTL
}) => {
  const { t } = useTranslation("productDetails");
  const { isSmallScreen } = useIsSmallScreen();
  const handleConfirmDialog = useCallback(() => {
    setAlertMessage("");
  }, [setAlertMessage]);

  if (!productId) return null;

  const isPriceAvailable = item?.isPriceAvailableInUserCountry;

  return (
    <>
      <PageSections sections={topSections} />

      <Page id="product-detail">
        <ProductDetailsBreadcrumbs />
        <div
          className={cn("product_detail_wrapper", {
            arabic: isRTL
          })}
        >
          <PDPHelmet />
          <div className="quickview_divider flex">
            <View.mobile>
              <ErrorBoundary fallback={null}>
                <TopMobileContent
                  itemDetailWrapperRef={itemDetailWrapperRef}
                  selectedSizeFullData={selectedSizeFullData}
                />
              </ErrorBoundary>
            </View.mobile>

            <ProductDetailImage shareButton thumbLength={6} key={item.id} />
            <div className="item_details_wrapper" ref={itemDetailWrapperRef}>
              <BrandDetails />
              {isPriceAvailable && <ProductPriceSection />}
              <View.mobile>
                <ErrorBoundary fallback={null}>
                  <ProductErrorInfo
                    product={item}
                    selectedSizeFullData={selectedSizeFullData}
                    productSizeChart={productSizeChart}
                    showProductSizeChart={showProductSizeChart}
                    showShippingOptionsModal={() => {
                      setShowShippingOptions(true);
                    }}
                  />
                </ErrorBoundary>
              </View.mobile>

              <PageSections sections={firstSections} />
              <GlobalShipping
                globalShipping={item?.highlights?.globalShipping}
              />
              {isPriceAvailable ? (
                <>
                  <View.desktop>
                    <ErrorBoundary fallback={null}>
                      <ProductWebSizes
                        productDetails={item}
                        productSizeChart={productSizeChart}
                        activeSizeType={activeSizeType}
                        setActiveSizeType={setActiveSizeType}
                        selectedSizeFullData={selectedSizeFullData}
                        setSelectedSizeFullData={setSelectedSizeFullData}
                        showProductSizeChart={() =>
                          setShowProductSizeChart(true)
                        }
                        showShippingOptionsModal={() => {
                          setShowShippingOptions(true);
                        }}
                      />
                    </ErrorBoundary>
                  </View.desktop>

                  <div className="action_container mt-4">
                    <View.mobile>
                      <ProductQuantityMob
                        selectedSize={selectedSizeFullData.size}
                        setSelectedQty={setSelectedQty}
                        selectedQty={selectedQty}
                      />
                    </View.mobile>
                    <MobileAddToCart
                      onAddToCartClickHandler={onAddToCartClickHandler}
                      className="d-none-desktop"
                    />

                    <View.desktop element="span">
                      <ProductQuantity
                        product={item}
                        selectedSize={selectedSizeFullData.size}
                        setSelectedQty={setSelectedQty}
                        selectedQty={selectedQty}
                      />

                      <AddToCart
                        product={item}
                        handleAddToCart={() => addToCart(getCartData())}
                        selectedSize={selectedSizeFullData.size}
                      />
                    </View.desktop>
                    <ErrorBoundary fallback={null}>
                      <AddToWishlistButton
                        product={item}
                        selectedSizeFullData={selectedSizeFullData}
                      />
                    </ErrorBoundary>
                    {item?.modelProfile && (
                      <View.desktop>
                        <ModelDetails modelProfile={item.modelProfile} />
                      </View.desktop>
                    )}
                    <SuccessAddedToCartMessage
                      selectedQty={selectedQty}
                      isSuccessBlockOpen={isSuccessBlockOpen}
                      closeSuccessAddedToCartBlock={
                        closeSuccessAddedToCartBlock
                      }
                      sameSizeInCartCount={sameSizeInCartCount}
                    />
                  </div>
                </>
              ) : (
                <IsNotPriceAvailableMessage />
              )}

              <PdpSyteDiscoveryBanner />
              {item?.modelProfile && (
                <View.mobile>
                  <ModelDetails modelProfile={item.modelProfile} />
                </View.mobile>
              )}
              {isPriceAvailable && (
                <>
                  <PayLaterOptions payLaterOptions={payLaterOptions} />
                  <RelatedProducts product={item} />
                  {isSmallScreen ? (
                    <ErrorBoundary fallback={null}>
                      <ProductMobileSizes
                        activeSizeType={activeSizeType}
                        setActiveSizeType={setActiveSizeType}
                        selectedSizeFullData={selectedSizeFullData}
                        onSizeSelectionHandler={onSizeSelectionHandler}
                        showProductSizeChart={() =>
                          setShowProductSizeChart(true)
                        }
                        openMultipleSizes={openMultipleSizes}
                        setOpenMultipleSizes={setOpenMultipleSizes}
                      />
                    </ErrorBoundary>
                  ) : null}
                </>
              )}

              <PageSections sections={secondSections} />

              <View.mobile className="mobile-view productExtraInfo">
                <MobileHighlightsWrapper />
                <ErrorBoundary fallback={null}>
                  <DeliveryAndReturnMobile />
                </ErrorBoundary>
                <SizeChart setShowProductSizeChart={setShowProductSizeChart} />
                <LoyaltyPointsInfo />
                <ScrollToTop />
              </View.mobile>
              <View.desktop>
                <ProductHighlights />
              </View.desktop>
            </div>
          </div>
          <SyteSimilarItems
            key={`syte_${language}_${countryId}`}
            pId={productId}
            subcatid={subcategory.id}
            imageUrl={`${appSettings.imageBaseURL}${captionImageURL}`}
            configSyteSettings={configSyteSettings}
          />
          {showProductSizeChart && (
            <ErrorBoundary fallback={null}>
              <ProductSizeChart
                handleSizeChartPopup={setShowProductSizeChart}
              />
            </ErrorBoundary>
          )}
          {showShippingOptions && (
            <ErrorBoundary fallback={null}>
              <ProductShippingOptions
                hideShippingOptionsModal={() => {
                  setShowShippingOptions(false);
                }}
                isModalShow={showShippingOptions}
              />
            </ErrorBoundary>
          )}
          <ErrorBoundary fallback={null}>
            <RecentlyViewedProducts />
          </ErrorBoundary>
        </div>
        <DialogModal
          showModal={Boolean(alertMessage)}
          titleText={t("alert")}
          bodyText={alertMessage}
          confirmButtonText={t("Ok")}
          onConfirm={handleConfirmDialog}
          onCancel={goToCart}
          cancelButtonText={t("viewCart")}
        />
      </Page>
    </>
  );
};

export default ProductDetailsLayout;
